/**
 * The assessments board, carried to the platform as test records (build 7).
 *
 * Operations control › Assessments kept its register in memory. The platform
 * already holds exactly this — a test carried out, by whom, against what
 * standard, with its verdict and when the next is due — so a filed assessment
 * becomes a test record, and an edit becomes that record's next revision (a
 * record an auditor read is never rewritten).
 *
 * Which test type: the tenant's own judged type of the same kind where it has
 * one (friction, structural); otherwise the catch-all `assessment` type
 * (migration 0094). The board's own kind and result travel on the record
 * (`summary.board_type`, `summary.result`), so the board reads back exactly
 * what was filed.
 *
 * The verdict, from the board's five results:
 *   pass → pass; pass with observations, conditional → attention;
 *   fail → fail; information → not assessed, with the reason — never a pass.
 *
 * Attachments are not carried: the board keeps a file in the page, and the
 * test record's own files are added on Testing records.
 *
 * Nothing here imports the demo domain: this sits in the entry chunk.
 */
import type { NewTestInput } from "../../../domain/testing";
import { t } from "../../../i18n";
import { registerWrite, type WriteAck, type WriteContext } from "../writes";

/** An assessment as the board keeps it (`ASSESSMENTS[]`). */
export interface BoardAssessment {
  id: string;
  type: string;
  title: string;
  area?: string;
  date: string;
  by?: string;
  std?: string;
  result: string;
  summary?: string;
  next?: string;
  values?: { k: string; v: string }[];
}

export const CATCH_ALL = "assessment";

/** The board's result → the platform's verdict. */
export function verdictOf(result: string): NonNullable<NewTestInput["outcome"]> {
  switch ((result || "").toLowerCase()) {
    case "pass":
      return "pass";
    case "fail":
      return "fail";
    case "information":
      return "not_assessed";
    default:
      /* pass with observations, conditional: something to watch */
      return "attention";
  }
}

/** Why the verdict is what it is: the assessor's findings, or what the board filed. */
export function basisOf(a: BoardAssessment): string {
  const said = (a.summary ?? "").trim();
  if (verdictOf(a.result) === "not_assessed")
    return said ? `Filed for information; no verdict given. ${said}` : "Filed for information; no verdict given.";
  return said || `Filed on the assessments board as "${a.result}".`;
}

/**
 * The test type an assessment is filed against: the tenant's judged type of
 * the same kind, else the catch-all. Undefined when the tenant has neither.
 */
export function testTypeFor(kind: string, types: readonly { key: string; mode: string }[]): string | undefined {
  const own = types.find((x) => x.key === kind && x.mode === "judged");
  if (own) return own.key;
  return types.some((x) => x.key === CATCH_ALL) ? CATCH_ALL : undefined;
}

/** An assessment as a test record. */
export function assessmentBody(a: BoardAssessment, testType: string): NewTestInput {
  const measures = (a.values ?? []).filter((v) => (v.k ?? "").trim() || (v.v ?? "").trim());
  return {
    testType,
    performedOn: a.date,
    title: a.title.trim(),
    ...(a.by?.trim() ? { performedBy: a.by.trim() } : {}),
    ...(a.std?.trim() ? { method: a.std.trim() } : {}),
    ...(a.area?.trim() ? { scope: a.area.trim() } : {}),
    /* the board's own kind, result and measures, read back exactly as filed */
    summary: {
      board_type: a.type,
      result: a.result,
      ...(measures.length ? { measures: measures as unknown as string } : {}),
    },
    results: [],
    outcome: verdictOf(a.result),
    outcomeBasis: basisOf(a),
    ...(a.summary?.trim() ? { notes: a.summary.trim() } : {}),
    ...(a.next ? { nextDue: a.next } : {}),
    source: "form",
  };
}

async function typeFor(ctx: WriteContext, a: BoardAssessment): Promise<string> {
  const types = await ctx.source.listTestTypes();
  const key = testTypeFor(a.type, types);
  if (!key) throw new Error(t("The platform has no test type for assessments yet. Ask an administrator to add it."));
  return key;
}

const ack = (id: string, reference: string): WriteAck => ({
  id,
  reference,
  kind: "test",
  text: t("{ref} saved on the platform as a test record.", { ref: reference }),
});

const INVALIDATE = (siteId: string) => [
  ["test-records", siteId],
  ["testing-summary", siteId],
];

registerWrite("assessment.add", {
  permission: () => "test:write",
  invalidate: INVALIDATE,
  async send(ctx, rec: BoardAssessment) {
    const r = await ctx.source.createTestRecord(ctx.siteId, assessmentBody(rec, await typeFor(ctx, rec)));
    return ack(r.id, r.reference);
  },
});

/** An edit: the next revision of the record it was filed as, or the first filing of one never saved. */
registerWrite("assessment.edit", {
  permission: () => "test:write",
  invalidate: INVALIDATE,
  async send(ctx, rec: BoardAssessment, extra: { id?: string; changes?: string[] } | null) {
    const body = assessmentBody(rec, await typeFor(ctx, rec));
    if (!extra?.id) {
      const r = await ctx.source.createTestRecord(ctx.siteId, body);
      return ack(r.id, r.reference);
    }
    const changed = (extra.changes ?? []).filter(Boolean);
    const r = await ctx.source.reviseTestRecord(extra.id, {
      ...body,
      reason: changed.length ? `Edited on the assessments board: ${changed.join(", ")}.` : "Edited on the assessments board.",
    });
    return {
      ...ack(r.id, r.reference),
      text: t("{ref} corrected on the platform (revision {n}).", { ref: r.reference, n: r.revision }),
    };
  },
});
